import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, withCtx as _withCtx, createBlock as _createBlock } from "vue";
const _hoisted_1 = {
  class: "px-4 py-2 my-2 rounded-sm bg-gray-100 w-full flex justify-between"
};
const _hoisted_2 = {
  "data-cy": "invoice-total-draft"
};
const _hoisted_3 = {
  class: "text-green-light mr-2"
};
const _hoisted_4 = {
  key: 0
};
const _hoisted_5 = {
  key: 1,
  "data-cy": "trust-balance-draft"
};
const _hoisted_6 = {
  key: 2,
  "data-cy": "client-balance-draft"
};
const _hoisted_7 = {
  key: 3
};
import { ref, computed, onMounted } from 'vue';
import { useInvoiceEditorStore } from '@/modules/billing/invoice/services/useInvoiceEditorStore.js';
import { formatCurrency } from '@/services/utils';
import { useInvoiceState } from '@/modules/billing/review/editDraft/useInvoiceState';
import { useMeStore } from '@/services/UserService/useMeStore';
import { storeToRefs } from 'pinia';
import LlTooltip from '@/components/LlTooltip.vue';
import EventTracker from '@/services/eventTracker';
const DEMO_TRUST_BALANCE = 500;
const DEMO_CLIENT_BALANCE = 1000;
export default {
  __name: 'InvoiceBalances',
  props: {
    tracking: {
      type: Object,
      required: false
    }
  },
  setup(__props) {
    const props = __props;
    const {
      totals,
      state,
      client,
      matter,
      id,
      trust,
      setTrust,
      setAccountBalance,
      isSplitBilling
    } = useInvoiceState();
    const invoiceEditorStore = useInvoiceEditorStore();
    const meStore = useMeStore();
    const {
      company,
      features
    } = storeToRefs(meStore);
    const accounts = ref({
      trust: null,
      client: null,
      matter: null
    });
    const isDemoMode = features.value?.invoicingDemoMode && (company.value?.settings?.demoMode || false);

    // Computed properties for formatting and conditionals
    const formattedInvoiceTotal = computed(() => formatCurrency(totals?.amount));
    const formattedInvoiceBalance = computed(() => formatCurrency(state?.balance));
    const formattedTrustBalance = computed(() => {
      // Return dummy value if demo mode is enabled
      if (isDemoMode) {
        return formatCurrency(DEMO_TRUST_BALANCE);
      }
      return formatCurrency(accounts.value.trust?.balance);
    });
    const formattedClientBalance = computed(() => {
      if (isDemoMode) {
        return formatCurrency(DEMO_CLIENT_BALANCE);
      }
      return formatCurrency(accounts.value.client?.balance);
    });
    const formattedMatterBalance = computed(() => formatCurrency(accounts.value.matter?.balance));
    const shouldShowInvoiceBalance = computed(() => !state?.draft && state?.balance && totals?.amount !== state?.balance);
    const trustExists = computed(() => !!trust || isDemoMode);
    const clientBalanceExists = computed(() => client?.billing?.billClient && accounts.value.client?.balance !== undefined || isDemoMode);
    const matterBalanceExists = computed(() => !client?.billing?.billClient && accounts.value.matter?.balance !== undefined);

    // Fetch accounting info on mount
    onMounted(async () => {
      if (!id) return;
      await fetchAccountingInfo();
    });

    // Async function to fetch accounting information
    const fetchAccountingInfo = async () => {
      const billing = matter && !client.billing?.billClient ? matter.billing : client.billing;
      await Promise.all([getAccountingClient(billing), handleTrustAccount(billing)]);
    };
    const getAccountingClient = async billing => {
      const accounting = matter?.accounting || client?.accounting;
      if (accounting) {
        const data = await invoiceEditorStore.getAccountingClient(accounting.id);
        setAccountBalance(data.balance);
        if (!billing?.billClient) {
          accounts.value.matter = data;
        } else {
          accounts.value.client = data;
        }
      }
    };
    const handleTrustAccount = async billing => {
      if (billing?.trust) {
        const data = await invoiceEditorStore.getAccountingAccount(billing.trust.account.id);
        setTrust(data, billing.evergreen);
        accounts.value.trust = data;
      }
    };
    const trackQBOBalanceTooltip = balanceType => {
      if (props.tracking && isDemoMode) {
        EventTracker.trackEvent(props.tracking, 'QBO Balance', 'Tooltip Shown', {
          balanceType,
          sourceContext: 'trainingFlow',
          invoiceId: id
        });
      }
    };
    return (_ctx, _cache) => {
      return _openBlock(), _createElementBlock("div", _hoisted_1, [_createElementVNode("div", _hoisted_2, [_cache[2] || (_cache[2] = _createTextVNode(" Invoice total: ", -1)), _createElementVNode("span", _hoisted_3, _toDisplayString(formattedInvoiceTotal.value), 1)]), shouldShowInvoiceBalance.value ? (_openBlock(), _createElementBlock("div", _hoisted_4, [_cache[3] || (_cache[3] = _createTextVNode(" Invoice balance: ", -1)), _createElementVNode("span", null, _toDisplayString(formattedInvoiceBalance.value), 1)])) : _createCommentVNode("", true), trustExists.value && !_unref(isSplitBilling) ? (_openBlock(), _createElementBlock("div", _hoisted_5, [_cache[5] || (_cache[5] = _createElementVNode("img", {
        src: "/img/qb16x16.png",
        class: "no-print"
      }, null, -1)), _cache[6] || (_cache[6] = _createTextVNode(" Trust balance: ", -1)), _createElementVNode("span", null, _toDisplayString(formattedTrustBalance.value), 1), _unref(isDemoMode) ? (_openBlock(), _createBlock(LlTooltip, {
        key: 0,
        icon: "help",
        class: "ml-1",
        onMouseenter: _cache[0] || (_cache[0] = $event => trackQBOBalanceTooltip('trust'))
      }, {
        default: _withCtx(() => [...(_cache[4] || (_cache[4] = [_createTextVNode(" Demo value - actual balance will sync once QuickBooks is connected ", -1)]))]),
        _: 1
      })) : _createCommentVNode("", true)])) : _createCommentVNode("", true), clientBalanceExists.value && !_unref(isSplitBilling) ? (_openBlock(), _createElementBlock("div", _hoisted_6, [_cache[8] || (_cache[8] = _createElementVNode("img", {
        src: "/img/qb16x16.png",
        class: "no-print"
      }, null, -1)), _cache[9] || (_cache[9] = _createTextVNode(" Client balance: ", -1)), _createElementVNode("span", null, _toDisplayString(formattedClientBalance.value), 1), _unref(isDemoMode) ? (_openBlock(), _createBlock(LlTooltip, {
        key: 0,
        icon: "help",
        class: "ml-1",
        onMouseenter: _cache[1] || (_cache[1] = $event => trackQBOBalanceTooltip('client'))
      }, {
        default: _withCtx(() => [...(_cache[7] || (_cache[7] = [_createTextVNode(" Demo value - actual balance will sync once QuickBooks is connected ", -1)]))]),
        _: 1
      })) : _createCommentVNode("", true)])) : _createCommentVNode("", true), matterBalanceExists.value ? (_openBlock(), _createElementBlock("div", _hoisted_7, [_cache[10] || (_cache[10] = _createElementVNode("img", {
        src: "/img/qb16x16.png",
        class: "no-print"
      }, null, -1)), _cache[11] || (_cache[11] = _createTextVNode(" Matter balance: ", -1)), _createElementVNode("span", null, _toDisplayString(formattedMatterBalance.value), 1)])) : _createCommentVNode("", true)]);
    };
  }
};